import { useEffect } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

// Static SEO fallbacks so the page always carries proper metadata, even
// before the database loads or if an admin leaves a field empty.
const FALLBACK_TITLE = "Top Gear Auto Spares | Used Engines, Transmissions & Parts";
const FALLBACK_DESCRIPTION =
  "Tested used engines, transmissions and body parts from Top Gear Auto Spares, Darwin. Quality second-hand car parts shipped Australia-wide with warranty.";
const FALLBACK_KEYWORDS =
  "used engines, used transmissions, auto body parts, second hand car parts, Darwin auto spares";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Single source of truth for the page <title>, meta description/keywords,
 * canonical URL, Open Graph tags, and the optional Google Ads snippet.
 * Values are pulled live from the database (site_settings) so admins can
 * edit them in the dashboard and see the changes immediately on the public site.
 */
export default function HeadManager() {
  const { site, seo, ads } = useSiteSettings();

  useEffect(() => {
    document.title = seo.meta_title || site.site_name || FALLBACK_TITLE;

    upsertMeta("name", "description", seo.meta_description || FALLBACK_DESCRIPTION);
    upsertMeta("name", "keywords", seo.meta_keywords || FALLBACK_KEYWORDS);
    if (seo.robots) upsertMeta("name", "robots", seo.robots);
    if (seo.canonical_url) upsertCanonical(seo.canonical_url);
    if (seo.og_title) upsertMeta("property", "og:title", seo.og_title);
    if (seo.og_description) upsertMeta("property", "og:description", seo.og_description);
    if (seo.og_image) {
      upsertMeta("property", "og:image", seo.og_image);
      upsertMeta("property", "og:image:width", "1200");
      upsertMeta("property", "og:image:height", "630");
      upsertMeta("name", "twitter:image", seo.og_image);
    }
  }, [seo, site.site_name]);

  useEffect(() => {
    // Remove any previously injected Google Ads scripts before re-injecting.
    document.head
      .querySelectorAll('script[data-readdy-ads]')
      .forEach((s) => s.remove());

    if (!ads.enabled || !ads.conversion_id.trim()) return;

    const src = `https://www.googletagmanager.com/gtag/js?id=${ads.conversion_id.trim()}`;

    const loader = document.createElement("script");
    loader.async = true;
    loader.src = src;
    loader.setAttribute("data-readdy-ads", "gtag");
    document.head.appendChild(loader);

    const config = document.createElement("script");
    config.setAttribute("data-readdy-ads", "config");
    config.textContent = `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${ads.conversion_id.trim()}');`;
    document.head.appendChild(config);

    if (ads.script.trim()) {
      const custom = document.createElement("script");
      custom.setAttribute("data-readdy-ads", "custom");
      custom.textContent = ads.script;
      document.head.appendChild(custom);
    }
  }, [ads.enabled, ads.conversion_id, ads.script]);

  return null;
}